import React, { useContext, useState, useEffect, Dispatch, SetStateAction } from 'react'
import { InstructorSearchResultInfo } from '@interfaces/Instructor'
import {
  IPDeleteRelatedInstructorsPayload,
  IPDeleteRelatedInstructorsResponse,
  IPPutRelatedInstructorsPayload,
  IPPutRelatedInstructorsResponse,
} from '../interface'
import { useMutation } from '@apollo/react-hooks'
import { MutationFunctionOptions } from '@apollo/react-common'
import { DELETE_RELATED_INSTRUCTOR, PUT_RELATED_INSTRUCTORS } from '../mutations'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import { StoreProps } from '@interfaces/StoreState'
import PortalPrograms from '@classes/Programs'
import { SCHEDULING_LEVEL } from '@utils/constants'

export interface IPFaveProvider {
  children?: React.ReactNode
  studentId: string
  programId: string
  faveInstructors: InstructorSearchResultInfo[]
  rawInstructors?: InstructorSearchResultInfo[]
}

export type IGetPayloadType = 'put' | 'delete'

export interface IFaveContext extends IPFaveProvider {
  isFaved: (InstructorId?: string) => boolean
  isEditMode: boolean
  setFaveInstructors: Dispatch<SetStateAction<InstructorSearchResultInfo[]>>
  setIsEditMode: Dispatch<SetStateAction<boolean>>
  getPayload: (
    type: IGetPayloadType,
    faveInstructors: InstructorSearchResultInfo[]
  ) => IPPutRelatedInstructorsPayload | IPDeleteRelatedInstructorsPayload | undefined
  putRelatedInstructors: (options?: MutationFunctionOptions<IPPutRelatedInstructorsResponse> | undefined) => void
  deleteRelatedInstructors: (options?: MutationFunctionOptions<IPDeleteRelatedInstructorsResponse> | undefined) => void
  enableActions?: boolean
}

const FaveContext = React.createContext<IFaveContext>({} as IFaveContext)

export function useFave() {
  return useContext(FaveContext)
}

export const FaveProvider: React.FC<IPFaveProvider> = ({
  studentId: StudentId = '',
  programId = '',
  faveInstructors: faveInstructorsProp = [],
  rawInstructors = [],
  children,
}) => {
  const Type = 'Preferred'
  const [faveInstructors, setFaveInstructors] = useState(faveInstructorsProp)
  const [isEditMode, setIsEditMode] = useState(false)
  const StudentPrograms = useSelector(
    ({ userProfile }: StoreProps) =>
      new PortalPrograms(userProfile?.info?.StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? [])
  )
  const program = StudentPrograms.getProgram(programId)
  const enableActions = program?.program?.IsGroup ? program?.program?.IsLead : !program?.program?.IsGroup

  useEffect(() => {
    setFaveInstructors(faveInstructorsProp)
  }, [faveInstructorsProp])

  const isFaved = (InstructorId?: string) =>
    !isEmpty(InstructorId) && faveInstructors.some((fave) => fave.InstructorId === InstructorId)

  const getPayload = (type: IGetPayloadType = 'put', faveInstructors: InstructorSearchResultInfo[] = []) => {
    if (type === 'delete') {
      return {
        payload: faveInstructors.map(({ InstructorId = '' }) => ({
          InstructorId,
          StudentId,
          Type,
          ProgramId: programId,
        })),
      }
    }

    if (type === 'put') {
      let schedulingLevel: {
        SchedulingLevel: string
        ProgramId?: string
      } = ({ SchedulingLevel: SCHEDULING_LEVEL.PROFILE_LEVEL } = {
        SchedulingLevel: SCHEDULING_LEVEL.PROFILE_LEVEL,
      })

      if (program?.program?.IsGroup) {
        schedulingLevel = {
          SchedulingLevel: SCHEDULING_LEVEL.PROGRAM_LEVEL,
          ProgramId: programId,
        }
      }

      return {
        putRelatedInstructorsInput: [
          {
            RelatedInstructorInfo: {
              ...schedulingLevel,
              InstructorId: faveInstructors[0].InstructorId || '',
              StudentId,
              Type,
              Origin: 'Portal',
            },
          },
        ],
      }
    }
  }

  const [putRelatedInstructors] = useMutation<IPPutRelatedInstructorsResponse>(PUT_RELATED_INSTRUCTORS)
  const [deleteRelatedInstructors] = useMutation<IPDeleteRelatedInstructorsResponse>(DELETE_RELATED_INSTRUCTOR)

  return (
    <FaveContext.Provider
      value={{
        studentId: StudentId,
        programId,
        faveInstructors,
        rawInstructors,
        isFaved,
        isEditMode,
        setFaveInstructors,
        setIsEditMode,
        getPayload,
        putRelatedInstructors,
        deleteRelatedInstructors,
        enableActions,
      }}
    >
      {children}
    </FaveContext.Provider>
  )
}

export default FaveProvider
